import { useRef, useEffect } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { ArrowRight, Quote } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

/* ─────────────────────────────────────────────
   SCROLL-PINNED STORY — 5 acts over 500 vh
───────────────────────────────────────────── */
const acts = [
  {
    num: '01',
    label: 'The Beginning',
    heading: 'A Vision\nBorn in 2006',
    body: 'Renaissance Garden Schools was born from the passion of dedicated professionals who devoted their careers to molding young minds. The road was not easy — determined and highly resilient, our founders overcame obstacles that delayed the opening for over two years. But the dream never wavered.',
    accent: 'from-orange-500/20 to-amber-400/10',
    image: '/lovable-uploads/226ca07d-bbe9-41c3-85b9-1fee2fff5643.png',
  },
  {
    num: '02',
    label: 'The Founders',
    heading: 'Guided by\nPurpose',
    body: 'Mrs. Catherine Chinyere Onyiriuba, an accomplished educationist, envisioned a school that would combine academic excellence with strong moral values. Alongside co-founder Chibuzo Onyiriuba, they built not just a school — but a home where every child is known, valued, and challenged.',
    accent: 'from-blue-500/20 to-indigo-400/10',
    image: '/images/Mummy.jpg',
  },
  {
    num: '03',
    label: 'Our Mission',
    heading: 'Lead &\nInnovate',
    body: 'To be a leading private educational services provider in Nigeria and beyond — researching and acquiring new skills in foundation education, adapting to provide the same in the most efficient and passionate manner possible.',
    accent: 'from-emerald-500/20 to-teal-400/10',
    image: '/lovable-uploads/aaddff51-ad84-478d-ae31-df3861af57aa.png',
  },
  {
    num: '04',
    label: 'Our Vision',
    heading: 'Shape Every\nChild',
    body: 'We provide an unparalleled high standard of educational systems that stimulate rapid intellectual growth — instilling confidence and discipline in every child, molding them to interact favourably with their world and tolerate others gracefully.',
    accent: 'from-violet-500/20 to-purple-400/10',
    image: '/lovable-uploads/46a2abd0-374c-484d-8f2b-db9faefd5ffe.png',
  },
  {
    num: '05',
    label: 'Our Strength',
    heading: '"Our God,\nOur Strength"',
    body: 'Every achievement at Renaissance Garden Schools is rooted in faith, excellence, and character. From nursery to secondary, we walk with each student — building tomorrow\'s leaders with purpose, passion, and an unshakeable moral foundation.',
    accent: 'from-rose-500/20 to-pink-400/10',
    image: '/lovable-uploads/9cbec4b8-eb63-43e4-82a5-4e612fbff926.png',
  },
];

const StoryPinned = () => (
  <section className="bg-[#101c32] text-white py-12 sm:py-16" aria-label="Our Story">
    <div className="container mx-auto px-6 divide-y divide-white/15">
      {acts.map((act,index) => <article key={act.num} className="grid md:grid-cols-2 gap-8 lg:gap-16 items-center py-10 lg:py-14">
        <div className={index%2 ? 'md:order-2' : ''}>
          <p className="text-orange-300 text-xs font-bold tracking-[0.2em] uppercase mb-4">{act.num} · {act.label}</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-5">{act.heading.replace('\n',' ')}</h2>
          <p className="text-slate-300 text-base sm:text-lg leading-relaxed max-w-xl">{act.body}</p>
        </div>
        <div className={'overflow-hidden rounded-2xl aspect-[4/3] '+(index%2 ? 'md:order-1' : '')}>
          <img src={act.image} alt={act.label + ' — Renaissance Garden Schools'} className="w-full h-full object-cover" loading="lazy" />
        </div>
      </article>)}
    </div>
  </section>
);

/* ─────────────────────────────────────────────
   HERO
───────────────────────────────────────────── */
const Hero = () => {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '-12%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[420px] h-[60svh] sm:h-[65svh] overflow-hidden flex items-center justify-center">
      {/* Parallax BG */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110 will-change-transform">
        <img
          src="/images/RGS WEB HEADER.jpg"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#06060e]/70 via-[#06060e]/50 to-[#06060e]" />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y: contentY, opacity: heroOpacity }}
        className="relative z-10 text-center px-6 max-w-4xl mx-auto"
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-primary text-[11px] font-black tracking-[0.35em] uppercase mb-6"
        >
          Our Legacy
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.75, delay: 0.1 }}
          className="font-display font-black text-[clamp(3rem,10vw,7rem)] text-white tracking-tighter leading-[0.88] mb-8"
        >
          Our <span className="text-primary">Story</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="text-white/80 text-lg sm:text-xl font-medium max-w-xl mx-auto"
        >
          From a dream in 2006 to a thriving garden of knowledge — shaping minds with excellence, faith, and purpose.
        </motion.p>
      </motion.div>

      {/* Scroll nudge */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-[1px] h-10 bg-gradient-to-b from-white/40 to-transparent"
        />
      </motion.div>
    </section>
  );
};

/* ─────────────────────────────────────────────
   FOUNDERS
───────────────────────────────────────────── */
const founders = [
  {
    name: 'Mrs. Catherine Chinyere Onyiriuba',
    role: 'Founder & Visionary Leader',
    image: '/images/Mummy.jpg',
    bio: 'An accomplished educationist with a passion for molding young minds, Mrs. Onyiriuba envisioned a school that would combine academic excellence with strong moral values — and brought that vision to life.',
  },
  {
    name: 'Chibuzo Onyiriuba',
    role: 'Co-Founder & Director',
    image: '/images/DADDY.jpg',
    bio: 'A visionary leader with a passion for educational excellence, Chibuzo Onyiriuba has been instrumental in shaping the operational framework and culture of Renaissance Garden Schools.',
  },
];

const FoundersSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px 0px' });

  return (
    <section className="py-16 sm:py-24 md:py-36 bg-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/4 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-6">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 36 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-16 sm:mb-20"
        >
          <p className="text-primary text-[11px] font-black tracking-[0.32em] uppercase mb-5">The Guardians</p>
          <h2 className="font-display font-black text-[clamp(2.5rem,6vw,4.5rem)] text-zinc-900 tracking-tighter leading-[0.9] mb-6">
            Meet Our <span className="text-gradient">Founders</span>
          </h2>
          <p className="text-zinc-500 text-lg font-medium max-w-xl mx-auto leading-relaxed">
            The visionaries who turned a dream into a legacy.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {founders.map((f, i) => {
            const cardRef = useRef<HTMLDivElement>(null);
            const cardInView = useInView(cardRef, { once: true, margin: '-60px 0px' });
            return (
              <motion.div
                ref={cardRef}
                key={i}
                initial={{ opacity: 0, y: 40 }}
                animate={cardInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                className="group rounded-[2rem] overflow-hidden border border-zinc-100 shadow-[0_4px_40px_rgba(0,0,0,0.06)] hover:shadow-[0_20px_60px_rgba(0,0,0,0.12)] transition-shadow bg-white"
              >
                {/* Photo */}
                <div className="relative h-72 sm:h-80 overflow-hidden">
                  <motion.img
                    src={f.image}
                    alt={f.name}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 1.2 }}
                    className="w-full h-full object-cover transition-all duration-700"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        `https://ui-avatars.com/api/?name=${encodeURIComponent(f.name)}&background=random`;
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/80 via-transparent to-transparent" />
                  <div className="absolute bottom-6 left-6 text-white">
                    <p className="text-primary text-[10px] font-black tracking-widest uppercase mb-1">{f.role}</p>
                    <h3 className="font-display font-black text-xl sm:text-2xl tracking-tight leading-tight">{f.name}</h3>
                  </div>
                </div>
                {/* Bio */}
                <div className="p-6 sm:p-8 flex gap-4 items-start">
                  <Quote size={24} className="text-primary/20 flex-shrink-0 mt-1" />
                  <p className="text-zinc-500 font-medium leading-relaxed text-[15px] italic">{f.bio}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────
   VALUES
───────────────────────────────────────────── */
const values = [
  { title: 'God-Centered', text: 'We build our educational foundation on strong spiritual values and the fear of God.', color: 'bg-orange-50 border-orange-100' },
  { title: 'Excellence', text: 'We strive for the highest standards in every aspect of education and school life.', color: 'bg-blue-50 border-blue-100' },
  { title: 'Integrity', text: 'We uphold honesty, ethics, and moral uprightness in all our dealings.', color: 'bg-emerald-50 border-emerald-100' },
  { title: 'Innovation', text: 'We embrace creativity and modern approaches to teaching and learning.', color: 'bg-violet-50 border-violet-100' },
];

const ValuesSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px 0px' });

  return (
    <section className="py-16 sm:py-24 bg-[#f8f9fc] relative overflow-hidden">
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-zinc-200 to-transparent" />
      <div className="container mx-auto px-6">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 36 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-14"
        >
          <p className="text-primary text-[11px] font-black tracking-[0.32em] uppercase mb-5">What We Stand For</p>
          <h2 className="font-display font-black text-[clamp(2.2rem,6vw,4rem)] text-zinc-900 tracking-tighter leading-[0.9]">
            Core <span className="text-gradient">Values</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {values.map((v, i) => {
            const vRef = useRef<HTMLDivElement>(null);
            const vInView = useInView(vRef, { once: true, margin: '-60px 0px' });
            return (
              <motion.div
                ref={vRef}
                key={i}
                initial={{ opacity: 0, y: 40 }}
                animate={vInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -6, scale: 1.02 }}
                className={`rounded-2xl p-7 border ${v.color} transition-shadow hover:shadow-lg`}
              >
                <h4 className="font-black text-zinc-900 text-xl mb-3 tracking-tight">{v.title}</h4>
                <p className="text-zinc-500 font-medium leading-relaxed text-[14px]">{v.text}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────
   STATS
───────────────────────────────────────────── */
const stats = [
  { value: '20+', label: 'Years of Excellence' },
  { value: '5,000+', label: 'Students Nurtured' },
  { value: 'Nursery–SS3', label: 'Full Academic Range' },
];

const StatsSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-60px 0px' });

  return (
    <section className="py-16 bg-[#07091a] relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.025]"
        style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '44px 44px' }}
      />
      <div className="container mx-auto px-6">
        <div ref={ref} className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-12 text-center max-w-3xl mx-auto">
          {stats.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="font-display font-black text-[clamp(2rem,5vw,3.5rem)] text-white tracking-tighter leading-none mb-2">
                {s.value}
              </p>
              <p className="text-white/40 text-[11px] font-bold tracking-widest uppercase">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────
   CTA
───────────────────────────────────────────── */
const CTASection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px 0px' });

  return (
    <section className="py-16 sm:py-24 md:py-36 bg-white relative overflow-hidden">
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-zinc-200 to-transparent" />

      <div className="container mx-auto px-6">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-[2.5rem] sm:rounded-[3rem] bg-[#07091a] overflow-hidden px-8 sm:px-14 md:px-20 py-16 sm:py-20 text-center"
        >
          {/* Ambient */}
          <div className="absolute top-0 right-0 w-80 h-80 bg-primary/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-0 w-60 h-60 bg-blue-600/8 rounded-full blur-[80px]" />
          <div className="absolute inset-0 opacity-[0.025]"
            style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '44px 44px' }}
          />

          <div className="relative z-10">
            <p className="text-primary text-[11px] font-black tracking-[0.32em] uppercase mb-5">Join Our Family</p>
            <h2 className="font-display font-black text-[clamp(2.2rem,6vw,4.5rem)] text-white tracking-tighter leading-[0.9] mb-6">
              Become Part of<br />Our <span className="text-primary">Story</span>
            </h2>
            <p className="text-white/50 text-lg font-medium max-w-xl mx-auto mb-10 leading-relaxed">
              Schedule a visit to experience our vibrant learning community firsthand.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="/#contact"
                whileHover={{ scale: 1.03, y: -3 }}
                whileTap={{ scale: 0.97 }}
                className="px-8 py-4 bg-primary text-white rounded-xl font-black text-base shadow-xl shadow-primary/30 flex items-center justify-center gap-2 group"
              >
                Contact Admissions
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </motion.a>
              <motion.a
                href="/"
                whileHover={{ scale: 1.03, y: -3 }}
                whileTap={{ scale: 0.97 }}
                className="px-8 py-4 bg-white/8 border border-white/15 text-white rounded-xl font-black text-base backdrop-blur-sm flex items-center justify-center"
              >
                Back to Home
              </motion.a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────
   PAGE
───────────────────────────────────────────── */
const About = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navigation />
      <main className="flex-grow">
        <Hero />
        <StoryPinned />
        <FoundersSection />
        <StatsSection />
        <ValuesSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default About;
